// Balance validation utilities to detect suspicious balances

// Thresholds for suspicious balances
const SUSPICIOUS_THRESHOLDS = {
  // Maximum reasonable balance for different token types
  MAX_BALANCES: {
    // Major cryptocurrencies (reasonable max balances)
    'BTC': 1000,           // 1000 BTC = ~$40M
    'ETH': 10000,          // 10,000 ETH = ~$20M
    'SOL': 100000,         // 100,000 SOL = ~$17M
    'USDT': 1000000,       // 1M USDT = $1M
    'USDC': 1000000,       // 1M USDC = $1M
    'DAI': 1000000,        // 1M DAI = $1M
    
    // Default for unknown tokens
    'DEFAULT': 1000000000, // 1B tokens (for small-cap tokens)
  },
  
  // Maximum reasonable market value (in USD)
  MAX_MARKET_VALUE: 10000000, // $10M per token
  
  // Minimum reasonable balance (to avoid dust)
  MIN_BALANCE: 0.000001,
  
  // Tokens that are known to have very large supplies (like meme coins)
  LARGE_SUPPLY_TOKENS: [
    'TULIDU', // Your token with large supply
    'BONK',
    'WIF',
    'DOGE',
    'SHIB',
    'PEPE',
    'FLOKI',
    'BABYDOGE',
    'SAFEMOON',
    'MOON',
    'ROCKET',
    'LAMBO',
    'MOONSHOT',
    'PUMP',
    'HODL',
    'DIAMOND',
    'GEM',
    'MOON',
    'STAR',
    'GOLD',
    'SILVER',
    'PLATINUM',
    'EMERALD',
    'RUBY',
    'SAPPHIRE'
  ]
};

/**
 * Check if a balance is suspicious and should be flagged
 * @param {Object} balance - Balance object with coin, total_balance, marketValue
 * @param {Object} options - Additional options
 * @returns {Object} - { isSuspicious: boolean, reason: string, severity: 'warning'|'error' }
 */
export function checkSuspiciousBalance(balance, options = {}) {
  const { coin, total_balance, marketValue } = balance;
  const coinUpper = coin?.toUpperCase();
  
  // Skip validation if balance is zero or negative
  if (!total_balance || total_balance <= 0) {
    return { isSuspicious: false, reason: null, severity: null };
  }
  
  const reasons = [];
  let severity = 'warning';
  
  // Check 1: Extremely large balance (billions+)
  const maxBalance = SUSPICIOUS_THRESHOLDS.MAX_BALANCES[coinUpper] || 
                    SUSPICIOUS_THRESHOLDS.MAX_BALANCES.DEFAULT;
  
  if (total_balance > maxBalance) {
    const isLargeSupplyToken = SUSPICIOUS_THRESHOLDS.LARGE_SUPPLY_TOKENS.includes(coinUpper);
    
    if (!isLargeSupplyToken) {
      reasons.push(`Balance (${total_balance.toLocaleString()}) exceeds reasonable maximum (${maxBalance.toLocaleString()})`);
      severity = 'error';
    } else {
      reasons.push(`Very large balance detected (${total_balance.toLocaleString()}) - verify if correct`);
    }
  }
  
  // Check 2: Extremely high market value
  if (marketValue && marketValue > SUSPICIOUS_THRESHOLDS.MAX_MARKET_VALUE) {
    reasons.push(`Market value ($${marketValue.toLocaleString()}) exceeds $${SUSPICIOUS_THRESHOLDS.MAX_MARKET_VALUE.toLocaleString()}`);
    severity = 'error';
  }
  
  // Check 3: Extremely small balance (dust)
  if (total_balance < SUSPICIOUS_THRESHOLDS.MIN_BALANCE) {
    reasons.push(`Balance (${total_balance}) is extremely small - may be dust`);
  }
  
  // Check 4: Specific checks for known tokens
  if (coinUpper === 'TULIDU') {
    // For Tulidu, check if balance is in the billions (which seems to be the case)
    if (total_balance > 1000000000) { // 1 billion
      reasons.push(`Tulidu balance (${total_balance.toLocaleString()}) appears to be total supply rather than wallet balance`);
      severity = 'error';
    }
  }
  
  // Check 5: Unrealistic price-to-balance ratio
  if (marketValue && total_balance) {
    const impliedPrice = marketValue / total_balance;
    
    // If implied price is extremely high or low, flag it
    if (impliedPrice > 1000000) { // $1M per token
      reasons.push(`Implied price ($${impliedPrice.toLocaleString()}) seems unrealistic`);
      severity = 'error';
    } else if (impliedPrice < 0.000001) { // $0.000001 per token
      reasons.push(`Implied price ($${impliedPrice}) seems unrealistic`);
    }
  }
  
  return {
    isSuspicious: reasons.length > 0,
    reason: reasons.join('; '),
    severity: severity,
    details: {
      balance: total_balance,
      marketValue: marketValue,
      maxBalance: maxBalance,
      isLargeSupplyToken: SUSPICIOUS_THRESHOLDS.LARGE_SUPPLY_TOKENS.includes(coinUpper)
    }
  };
}

/**
 * Filter out suspicious balances from portfolio totals
 * @param {Array} balances - Array of balance objects
 * @returns {Array} - Filtered balances excluding suspicious ones
 */
export function filterSuspiciousBalances(balances) {
  return balances.filter(balance => {
    const check = checkSuspiciousBalance(balance);
    return !check.isSuspicious || check.severity === 'warning'; // Keep warnings, exclude errors
  });
}

/**
 * Get total portfolio value excluding suspicious balances
 * @param {Array} balances - Array of balance objects
 * @returns {number} - Total portfolio value
 */
export function getSafePortfolioValue(balances) {
  const safeBalances = filterSuspiciousBalances(balances);
  return safeBalances.reduce((sum, balance) => {
    return sum + (balance.marketValue || 0);
  }, 0);
}

/**
 * Get suspicious balances for display with warnings
 * @param {Array} balances - Array of balance objects
 * @returns {Array} - Array of suspicious balance objects with warning info
 */
export function getSuspiciousBalances(balances) {
  return balances.map(balance => {
    const check = checkSuspiciousBalance(balance);
    return {
      ...balance,
      suspicious: check.isSuspicious,
      warningReason: check.reason,
      warningSeverity: check.severity,
      warningDetails: check.details
    };
  }).filter(balance => balance.suspicious);
}

export default {
  checkSuspiciousBalance,
  filterSuspiciousBalances,
  getSafePortfolioValue,
  getSuspiciousBalances
}; 